/** Read-only, source-bound follow-up evidence on the selected study run. No execution authority. */
import * as z from 'zod';
export const shopifyInquiryFindingSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().min(1),
    observableSignals: z.array(z.string()),
    suggestedEvidenceSources: z.array(z.string()),
});
export const shopifyInquiryExchangeSchema = z.object({
    respondentId: z.string().min(1),
    questionId: z.string().min(1),
    question: z.string().min(1),
    originalAnswer: z.string(),
    followups: z.array(z.object({
        question: z.string().min(1),
        answer: z.string().optional(),
        status: z.enum(['succeeded', 'failed']),
        positionTransition: z
            .enum([
            'retained_with_clarification',
            'retained_with_stronger_support',
            'qualified',
            'revised_with_new_support',
            'revised_without_new_support',
            'contradicted_original',
            'indeterminate',
        ])
            .optional(),
        warning: z.string().optional(),
    })),
    findings: z.array(shopifyInquiryFindingSchema),
});
export const shopifyInquiryRunSchema = z.object({
    runId: z.string().min(1),
    armId: z.string().min(1),
    armLabel: z.string().min(1),
    status: z.enum(['pending', 'running', 'completed', 'partial', 'failed', 'stale', 'unavailable']),
    message: z.string().min(1),
    requestedAt: z.string(),
    finishedAt: z.string().nullable(),
    noProbeRequiredCount: z.number().int().nonnegative(),
    exchanges: z.array(shopifyInquiryExchangeSchema),
});
/** A saved proposed question, not a delivered follow-up or an execution grant. */
export const shopifyInquiryProposalSchema = z.object({
    id: z.string().min(1),
    armId: z.string().min(1),
    armLabel: z.string().min(1),
    assessmentRunId: z.string().min(1),
    sourceChildRunId: z.string().min(1),
    sourcePoint: z
        .object({
        persona_id: z.string().min(1),
        question_id: z.string().min(1),
        source_hash: z.string().min(1),
        assessment_artifact_hash: z.string().min(1),
    })
        .strict(),
    question: z.string().min(1),
    missingDistinction: z.string().min(1),
    focusQuote: z.string().min(1),
    answers: z
        .array(z.object({
        questionId: z.string().min(1),
        question: z.string().min(1),
        response: z.string(),
        selected: z.boolean(),
    }))
        .min(1),
});
/** Durable per-conversation outcomes, distinct from the historical selected-answer run. */
export const respondentInquiryStatusSchema = z.enum([
    'original_pending',
    'original_failed',
    'decision_pending',
    'question_pending',
    'question_succeeded',
    'question_failed',
    'question_uncertain',
    'question_not_needed',
    'answer_pending',
    'skipped',
    'decision_failed',
    'decision_uncertain',
    'answer_succeeded',
    'answer_failed',
    'answer_uncertain',
    'task_failed',
    'cancelled',
]);
export const respondentInquiryConversationSchema = z.object({
    id: z.string().min(1),
    armId: z.string().min(1),
    armLabel: z.string().min(1),
    respondentId: z.string().min(1),
    sourceChildRunId: z.string().min(1),
    status: respondentInquiryStatusSchema,
    writerStatus: respondentInquiryStatusSchema.nullable(),
    decision: z
        .object({
        action: z.enum(['ask', 'skip']),
        basis: z.enum(['policy', 'model']),
        explanation: z.string().nullable(),
    })
        .nullable(),
    originalAnswers: z.array(z.object({ questionId: z.string(), question: z.string(), response: z.string() })),
    followup: z
        .object({
        questionId: z.string(),
        question: z.string(),
        answer: z.string().nullable(),
        sourceKey: z.string().nullable(),
        sourceHash: z.string().nullable(),
    })
        .nullable(),
});
export const shopifyStudyInquiryResultsSchema = z.object({
    availability: z.enum(['available', 'unavailable']),
    message: z.string().optional(),
    sourceRunId: z.string().min(1),
    runs: z.array(shopifyInquiryRunSchema),
    proposals: z.array(shopifyInquiryProposalSchema).optional(),
    durableFailure: z
        .object({
        status: z.literal('failed'),
        code: z.enum(['inquiry_shape_invalid', 'inquiry_evidence_invalid', 'inquiry_read_failed']),
    })
        .optional(),
    durable: z
        .object({
        coverage: z.enum(['complete', 'partial', 'pending']),
        /** The accepted set: respondents whose original survey answer succeeded. */
        conversations: z.array(respondentInquiryConversationSchema),
        /** Shoppers whose survey answer was not saved; not conversations, so Results state the reduced base. */
        failedSurveyAnswers: z.number().int().nonnegative().optional(),
        /** Bounded metadata while running; settled tasks may contain failed/uncertain artifacts, not successful answers. */
        progress: z
            .array(z.object({
            stage: z.enum(['question', 'answer']),
            accepted: z.number().int().nonnegative(),
            settled: z.number().int().nonnegative(),
            failed: z.number().int().nonnegative(),
            cancelled: z.number().int().nonnegative(),
            outstanding: z.number().int().nonnegative(),
        }))
            .optional(),
    })
        .optional(),
});
//# sourceMappingURL=shopify-study-inquiry-results.js.map